import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { reportError } from '../services/errorReporting';

interface AppErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface AppErrorBoundaryState {
  hasError: boolean;
}

const defaultFallback = (
  <Box sx={{ p: 4, maxWidth: 600, mx: 'auto', textAlign: 'center' }}>
    <Typography variant="h5" gutterBottom>
      Something went wrong
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
      The app hit an unexpected error. Your data is safe — it&apos;s stored
      locally on this device. Reloading usually resolves transient issues.
    </Typography>
    <Button
      variant="contained"
      onClick={() => {
        window.location.reload();
      }}
    >
      Reload
    </Button>
  </Box>
);

export default class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  override state: AppErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): AppErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, _info: ErrorInfo): void {
    // The component stack is intentionally not forwarded — it can include
    // prop strings that contain user content, and the exception itself
    // already carries the stack we need.
    reportError(error);
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return this.props.fallback ?? defaultFallback;
    }
    return this.props.children;
  }
}
